import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ROUTES } from "../../app/routes";
import axios from "axios";

interface DropdownApiResponse {
  success: boolean;
  data: {
    languages: Array<{
      language_dropdown_uid: number;
      language: string;
      created_at: string;
      updated_at: string;
    }>;
    locations: Array<{
      location_dropdown_uid: number;
      location: string;
      created_at: string;
      updated_at: string;
    }>;
    resourceGroups: Array<{
      resource_group_dropdown_uid: number;
      resource_group: string;
      created_at: string;
      updated_at: string;
    }>;
    ttecCommunities: Array<{
      ttec_communities_dropdown_uid: number;
      ttec_communities: string;
      created_at: string;
      updated_at: string;
    }>;
    leadershipCultures: Array<{
      leadership_culture_dropdown_uid: number;
      name: string;
      created_at: string;
      updated_at: string;
    }>;
  };
}

interface DropdownState {
  languages: DropdownApiResponse["data"]["languages"];
  locations: DropdownApiResponse["data"]["locations"];
  resourceGroups: DropdownApiResponse["data"]["resourceGroups"];
  ttecCommunities: DropdownApiResponse["data"]["ttecCommunities"];
  leadershipCultures: DropdownApiResponse["data"]["leadershipCultures"];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

export const fetchDropdowns = createAsyncThunk<
  DropdownApiResponse["data"],
  void
>("dropdowns/fetchDropdowns", async () => {
  try {
    const response = await axios({
      method: "get",
      url: ROUTES.getDropdownValues,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json"
      },
    });

    if (response.data && response.data.success === true && response.data.data) {
      return response.data.data;
    } else if (response.data && response.data.success === false) {
      throw new Error(response.data.message || "API returned success: false");
    } else {
      if (response.data) {
        if (
          response.data.languages ||
          response.data.locations ||
          response.data.resourceGroups ||
          response.data.ttecCommunities ||
          response.data.leadershipCultures
        ) {
          return response.data;
        }
      }
      throw new Error("Unexpected API response format");
    }
  } catch (error) {
    throw error;
  }
});

const initialState: DropdownState = {
  languages: [],
  locations: [],
  resourceGroups: [],
  ttecCommunities: [],
  leadershipCultures: [],
  status: "idle",
  error: null,
};

const fetchDropdownsSlice = createSlice({
  name: "fetchDropdowns",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDropdowns.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDropdowns.fulfilled, (state, action) => {
        state.status = "succeeded";

        if (action.payload) {
          state.languages = action.payload.languages || [];
          state.locations = action.payload.locations || [];
          state.resourceGroups = action.payload.resourceGroups || [];
          state.ttecCommunities = action.payload.ttecCommunities || [];
          state.leadershipCultures = action.payload.leadershipCultures || [];
        }

      })
      .addCase(fetchDropdowns.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch dropdown data";
      });
  },
});

export default fetchDropdownsSlice.reducer;
